import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  LinearProgress,
  Alert,
  InputAdornment,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { CarbonCareDatePicker } from '../shared/CarbonCareDatePicker';

interface FileUploadDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (formData: FormData) => Promise<void>;
  folderId: number;
}

const ACCEPTED_TYPES = '.pdf,.zip,.doc,.docx,.xls,.xlsx';

const FileUploadDialog: React.FC<FileUploadDialogProps> = ({
  open,
  onClose,
  onSubmit,
  folderId,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [changeDate, setChangeDate] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');


  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) {
      if (selected.size > 50 * 1024 * 1024) {
        setError('File size must be less than 50MB');
        return;
      }
      setFile(selected);
      if (!title) setTitle(selected.name.replace(/\.[^/.]+$/, ''));
      setError('');
    }
  };

  const handleSubmit = async () => {
    if (!file) return;
    setUploading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder_id', String(folderId));
      formData.append('title', title || file.name);
      if (changeDate) formData.append('change_date', changeDate);

      await onSubmit(formData);
      handleClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setTitle('');
    setChangeDate('');
    setError('');
    setUploading(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Upload File</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <Box
            sx={{
              border: '2px dashed',
              borderColor: file ? 'primary.main' : 'grey.300',
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              cursor: 'pointer',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
            }}
            onClick={() => document.getElementById('file-upload-input')?.click()}
          >
            <input
              id="file-upload-input"
              type="file"
              accept={ACCEPTED_TYPES}
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <UploadIcon sx={{ fontSize: 40, color: file ? 'primary.main' : 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color={file ? 'primary' : 'text.secondary'}>
              {file ? file.name : 'Click to select a file (PDF, ZIP, DOC, DOCX, XLS, XLSX)'}
            </Typography>
            {file && (
              <Typography variant="caption" color="text.secondary">
                {(file.size / (1024 * 1024)).toFixed(2)} MB
              </Typography>
            )}
          </Box>

          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
          />
          <CarbonCareDatePicker
            label="Document Date"
            value={changeDate || null}
            onChange={setChangeDate}
            fullWidth
            maxDate={new Date()}
            startAdornment={
              <InputAdornment position="start">
                <CalendarIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            }
          />

          {uploading && <LinearProgress />}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={uploading} variant="outlined">Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!file || uploading}>
          Upload
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FileUploadDialog;
